import {
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Button,
    HStack,
    Text,
    Icon,
    Box,
    Portal,
} from '@chakra-ui/react';
import { TbChevronDown, TbCheck, TbMessage, TbNote, TbX } from 'react-icons/tb';
import { useMemo, useRef } from 'react';
import StatusIcon, { getStatusLabel } from './StatusIcon';
import { useToggleOrderFlag } from '../hooks/useToggleOrderFlag';
import { useCompleteOrder } from '../hooks/useCompleteOrder';
import { useArchiveOrder } from '../hooks/useArchiveOrder';

interface StatusIndicatorProps {
    orderId: string;
    completed: boolean;
    manuallyFlagged: boolean;
    archived?: boolean;
    showLabel?: boolean;
    iconSize?: string;
    onOpenChat?: (orderId: string, mode: 'message' | 'note', anchorEl?: HTMLElement | null) => void;
    reviewedByCurrentUser?: boolean;
    onToggleReview?: (orderId: string, reviewed: boolean) => void;
}

const StatusIndicator = ({
    orderId,
    completed,
    manuallyFlagged,
    archived = false,
    showLabel = false,
    iconSize = '16px',
    onOpenChat,
    reviewedByCurrentUser = false,
    onToggleReview
}: StatusIndicatorProps) => {
    const toggleFlag = useToggleOrderFlag();
    const completeOrder = useCompleteOrder();
    const archiveOrder = useArchiveOrder();
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    const status = useMemo(() => {
        if (archived) return 'archived';
        if (completed) return 'completed';
        if (manuallyFlagged) return 'manually-flagged';
        return 'not-started';
    }, [archived, completed, manuallyFlagged]);

    const statusOptions = [
        { value: 'manually-flagged', label: 'Save for Later' },
        { value: 'completed', label: 'Completed' },
        { value: 'archived', label: 'Archive' },
    ] as const;

    return (
        <Menu>
            <MenuButton
                ref={menuButtonRef}
                as={Button}
                size="sm"
                variant="ghost"
                p={0}
                minW="auto"
                h="auto"
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
                onClick={(e) => e.stopPropagation()}
            >
                <HStack spacing={showLabel ? 2 : 0}>
                    <StatusIcon status={status} size={iconSize} />
                    {showLabel && (
                        <>
                            <Text fontSize="md" fontWeight="medium" color="gray.600">
                                {status === 'not-started' ? 'Active' : getStatusLabel(status)}
                            </Text>
                            <Icon as={TbChevronDown as React.ElementType} color="gray.500" />
                        </>
                    )}
                </HStack>
            </MenuButton>
            <Portal>
            <MenuList
                onClick={(e) => e.stopPropagation()}
                minW="220px"
                py={1}
                boxShadow="none"
                zIndex={1500}
            >
                {statusOptions.map((option) => {
                    const isCurrentStatus = status === option.value;

                    return (
                        <MenuItem
                            key={option.value}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (option.value === 'manually-flagged') {
                                    toggleFlag.mutate({ orderId });
                                } else if (option.value === 'completed') {
                                    completeOrder.mutate({ orderId });
                                } else if (option.value === 'archived') {
                                    archiveOrder.mutate({ orderId });
                                }
                            }}
                            _hover={{ bg: 'gray.50' }}
                            px={3}
                            py={2}
                            fontSize="sm"
                        >
                            <HStack spacing={3} w="100%">
                                <Box flexShrink={0}>
                                    <StatusIcon status={option.value} />
                                </Box>
                                <Text flex={1}>{option.label}</Text>
                                {isCurrentStatus && (
                                    <Icon as={TbCheck as React.ElementType} w={4} h={4} color="gray.600" />
                                )}
                            </HStack>
                        </MenuItem>
                    );
                })}
                {onToggleReview && (
                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleReview(orderId, !reviewedByCurrentUser);
                        }}
                        _hover={{ bg: 'gray.50' }}
                        px={3}
                        py={2}
                        fontSize="sm"
                    >
                        <HStack spacing={3} w="100%" align="center">
                            <Box flexShrink={0} display="flex" alignItems="center">
                                <Icon
                                    as={(reviewedByCurrentUser ? TbX : TbCheck) as React.ElementType}
                                    w={4}
                                    h={4}
                                    color="gray.500"
                                />
                            </Box>
                            <Text flex={1}>{reviewedByCurrentUser ? 'Remove My Review' : 'Mark Reviewed'}</Text>
                        </HStack>
                    </MenuItem>
                )}
                <MenuDivider />
                <MenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenChat?.(orderId, 'message', menuButtonRef.current);
                    }}
                    _hover={{ bg: 'gray.50' }}
                    px={3}
                    py={2}
                    fontSize="sm"
                >
                    <HStack spacing={3} w="100%" align="center">
                        <Box flexShrink={0} display="flex" alignItems="center">
                            <Icon as={TbMessage as React.ElementType} w={4} h={4} color="gray.500" />
                        </Box>
                        <Text flex={1}>Send Message</Text>
                    </HStack>
                </MenuItem>
                <MenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenChat?.(orderId, 'note', menuButtonRef.current);
                    }}
                    _hover={{ bg: 'gray.50' }}
                    px={3}
                    py={2}
                    fontSize="sm"
                >
                    <HStack spacing={3} w="100%" align="center">
                        <Box flexShrink={0} display="flex" alignItems="center">
                            <Icon as={TbNote as React.ElementType} w={4} h={4} color="gray.500" />
                        </Box>
                        <Text flex={1}>Add Note to Self</Text>
                    </HStack>
                </MenuItem>
            </MenuList>
            </Portal>
        </Menu>
    );
};

export default StatusIndicator;
